import config from "appConfig";

import { getLinkForCourseLesson } from "../../../../services/Utils";
import lesson0406 from "../lesson-06/lesson";
import lesson0420 from "../lesson-20/lesson";

const linkLesson0406 = getLinkForCourseLesson("e0db4477-1995-4380-9c35-2338dcf5e504", lesson0406.id);
const linkLesson0420 = getLinkForCourseLesson("e0db4477-1995-4380-9c35-2338dcf5e504", lesson0420.id);

const theory = `<h3 class=sectionHead>Introduction</h3>
<p>In this Lesson, the coupling between the mechanical and acoustic domains will be presented. In particular, the relationships between the physical quantities characterising the mechanical and acoustic systems are described and represented in the form of an equivalent electrical circuit [1, 3]. An example of the use of equivalent circuits is also proposed.
<h3 class=sectionHead>Constant-area coupling</h3>
<p>
   A rigid piston of area
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>S</mi>
         </mrow>
         <mrow>
            <mi>d</mi>
         </mrow>
      </msub>
   </math>
   is placed at the entrance of an infinite duct having the same cross-section area (<a href="#" data-img-alt="Figure 1. Rigid piston vibrating in a waveguide having the same area." data-img-src=${config.ASSETS_URL}/04/11/theory/figure-1.svg>Figure 1</a>). A force
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>F</mi>
   </math>
   is then applied to it and makes it vibrate with a velocity
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>V</mi>
   </math>. The air velocity in the vicinity of the interface is equal to
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>V</mi>
   </math>
   over the entire cross section of the tube. The flow rate
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>Q</mi>
   </math>
   is then equal to
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>Q</mi>
      <mo class=MathClass-rel>=</mo>
      <msub>
         <mrow>
            <mi class=MathClass-op>∫</mi>
            <mo>⁡</mo>
         </mrow>
         <mrow>
            <msub>
               <mrow>
                  <mi>S</mi>
               </mrow>
               <mrow>
                  <mi>d</mi>
               </mrow>
            </msub>
         </mrow>
      </msub>
      <mi>V</mi>
      <mi mathvariant=italic>dS</mi>
      <mo class=MathClass-rel>=</mo>
      <mi>V</mi>
      <msub>
         <mrow>
            <mi class=MathClass-op>∫</mi>
            <mo>⁡</mo>
         </mrow>
         <mrow>
            <msub>
               <mrow>
                  <mi>S</mi>
               </mrow>
               <mrow>
                  <mi>d</mi>
               </mrow>
            </msub>
         </mrow>
      </msub>
      <mi mathvariant=italic>dS</mi>
      <mo class=MathClass-rel>=</mo>
      <msub>
         <mrow>
            <mi>S</mi>
         </mrow>
         <mrow>
            <mi>d</mi>
         </mrow>
      </msub>
      <mi>V</mi>
   </math>. For a normal force, the pressure at the interface is given by
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>p</mi>
      <mo class=MathClass-rel>=</mo>
      <mi>F</mi>
      <mo class=MathClass-bin>∕</mo>
      <msub>
         <mrow>
            <mi>S</mi>
         </mrow>
         <mrow>
            <mi>d</mi>
         </mrow>
      </msub>
   </math>. These two equations reflect the coupling between mechanical (<math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>F</mi>
   </math>
   and
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>V</mi>
   </math>) and acoustic (<math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>p</mi>
   </math>
   and
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>Q</mi>
   </math>) quantities
<table class=equation>
   <tr>
      <td>
         <math class=equation xmlns=http://www.w3.org/1998/Math/MathML display=block>
            <mrow>
               <mo form=prefix fence=true>{</mo>
               <mrow>
                  <mtable style="" equalrows=false align=axis class=array columnlines=none equalcolumns=false>
                     <mtr>
                        <mtd columnalign=left class=array>
                           <mi>F</mi>
                           <mo class=MathClass-rel>=</mo>
                           <msub>
                              <mrow>
                                 <mi>S</mi>
                              </mrow>
                              <mrow>
                                 <mi>d</mi>
                              </mrow>
                           </msub>
                           <mi>p</mi>
                        </mtd>
                     </mtr>
                     <mtr>
                        <mtd columnalign=left class=array>
                           <msub>
                              <mrow>
                                 <mi>S</mi>
                              </mrow>
                              <mrow>
                                 <mi>d</mi>
                              </mrow>
                           </msub>
                           <mi>V</mi>
                           <mo class=MathClass-rel>=</mo>
                           <mi>Q</mi>
                        </mtd>
                     </mtr>
                  </mtable>
               </mrow>
            </mrow>
         </math>
      <td class=eq-no>(1)
</table>
<figure class=figure>
   <p>
      <img alt="Figure 1. Rigid piston vibrating in a waveguide having the same area." src=${config.ASSETS_URL}/04/11/theory/figure-1.svg>
      <figcaption class=caption>Figure 1. Rigid piston vibrating in a waveguide having the same area.</figcaption>
</figure>
<p>
   These two equations allow us to calculate the relationship between a mechanical impedance
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>Z</mi>
         </mrow>
         <mrow>
            <mi>m</mi>
         </mrow>
      </msub>
      <mo class=MathClass-rel>=</mo>
      <mi>F</mi>
      <mo class=MathClass-bin>∕</mo>
      <mi>V</mi>
   </math>
   and an acoustic impedance
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>Z</mi>
         </mrow>
         <mrow>
            <mi>a</mi>
         </mrow>
      </msub>
      <mo class=MathClass-rel>=</mo>
      <mi>p</mi>
      <mo class=MathClass-bin>∕</mo>
      <mi>Q</mi>
   </math>
<table class=equation>
   <tr>
      <td>
         <math class=equation xmlns=http://www.w3.org/1998/Math/MathML display=block>
            <msub>
               <mrow>
                  <mi>Z</mi>
               </mrow>
               <mrow>
                  <mi>m</mi>
               </mrow>
            </msub>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <mi>F</mi>
               </mrow>
               <mrow>
                  <mi>V</mi>
               </mrow>
            </mfrac>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <mi>p</mi>
                  <msub>
                     <mrow>
                        <mi>S</mi>
                     </mrow>
                     <mrow>
                        <mi>d</mi>
                     </mrow>
                  </msub>
               </mrow>
               <mrow>
                  <mi>Q</mi>
                  <mo class=MathClass-bin>∕</mo>
                  <msub>
                     <mrow>
                        <mi>S</mi>
                     </mrow>
                     <mrow>
                        <mi>d</mi>
                     </mrow>
                  </msub>
               </mrow>
            </mfrac>
            <mo class=MathClass-rel>=</mo>
            <msubsup>
               <mrow>
                  <mi>S</mi>
               </mrow>
               <mrow>
                  <mi>d</mi>
               </mrow>
               <mrow>
                  <mn>2</mn>
               </mrow>
            </msubsup>
            <mfrac>
               <mrow>
                  <mi>p</mi>
               </mrow>
               <mrow>
                  <mi>Q</mi>
               </mrow>
            </mfrac>
            <mo class=MathClass-rel>=</mo>
            <msubsup>
               <mrow>
                  <mi>S</mi>
               </mrow>
               <mrow>
                  <mi>d</mi>
               </mrow>
               <mrow>
                  <mn>2</mn>
               </mrow>
            </msubsup>
            <msub>
               <mrow>
                  <mi>Z</mi>
               </mrow>
               <mrow>
                  <mi>a</mi>
               </mrow>
            </msub>
         </math>
      <td class=eq-no>(2)
</table>
<p>
   The continuity equations of system (1) can be represented, from an electrical point of view, using the ideal transformer shown in <a href="#" data-img-alt="Figure 2. Equivalent transformer of a constant area acousto-mechanical interface." data-img-src=${config.ASSETS_URL}/04/11/theory/figure-2.svg>Figure 2</a>.
   <figure class=figure>
<p><img alt="Figure 2. Equivalent transformer of a constant area acousto-mechanical interface." src=${config.ASSETS_URL}/04/11/theory/figure-2.svg><figcaption class=caption>Figure 2. Equivalent transformer of a constant area acousto-mechanical interface.</figcaption></figure>
<h3 class=sectionHead>Coupling with a surface change</h3>
<p>
   Now, let’s consider the case of a rigid plane piston of cross sectional area
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>S</mi>
         </mrow>
         <mrow>
            <mi>d</mi>
         </mrow>
      </msub>
   </math>
   which vibrates at the entrance of a duct of cross sectional area
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>S</mi>
         </mrow>
         <mrow>
            <mi>w</mi>
         </mrow>
      </msub>
   </math>, with
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>S</mi>
         </mrow>
         <mrow>
            <mi>w</mi>
         </mrow>
      </msub>
      <mo class=MathClass-rel>></mo>
      <msub>
         <mrow>
            <mi>S</mi>
         </mrow>
         <mrow>
            <mi>d</mi>
         </mrow>
      </msub>
   </math>
   (see <a href="#" data-img-alt="Figure 3.Rigid piston vibrating in a waveguide with a section change." data-img-src=${config.ASSETS_URL}/04/11/theory/figure-3.svg>Figure 3</a>) [2]. The particle velocity field here has a more complex behaviour than in the constant cross-section case. This change is reflected in the following phenomena in the vicinity of the piston:
<ul class=itemize1>
   <li class=itemize>
      there is a continuity of the velocity field <strong><span class=cmbx-10>on</span></strong> the interface for
      <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
         <msub>
            <mrow>
               <mi>S</mi>
            </mrow>
            <mrow>
               <mi>d</mi>
            </mrow>
         </msub>
         <mo class=MathClass-bin>∩</mo>
         <msub>
            <mrow>
               <mi>S</mi>
            </mrow>
            <mrow>
               <mi>w</mi>
            </mrow>
         </msub>
      </math>. As the duct is rigid,
      <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
         <mi>V</mi>
         <mo class=MathClass-rel>=</mo>
         <mn>0</mn>
      </math>
      on the complement of
      <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
         <msub>
            <mrow>
               <mi>S</mi>
            </mrow>
            <mrow>
               <mi>d</mi>
            </mrow>
         </msub>
      </math>
      in
      <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
         <msub>
            <mrow>
               <mi>S</mi>
            </mrow>
            <mrow>
               <mi>w</mi>
            </mrow>
         </msub>
      </math>
      (<math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
         <msub>
            <mrow>
               <mi>S</mi>
            </mrow>
            <mrow>
               <mi>w</mi>
            </mrow>
         </msub>
         <mo class=MathClass-bin>∖</mo>
         <msub>
            <mrow>
               <mi>S</mi>
            </mrow>
            <mrow>
               <mi>d</mi>
            </mrow>
         </msub>
      </math>),
   <li class=itemize>in the vicinity of the piston, there is a transition zone which is highlighted by the pink zone in <a href="#" data-img-alt="Figure 3.Rigid piston vibrating in a waveguide with a section change." data-img-src=${config.ASSETS_URL}/04/11/theory/figure-3.svg>Figure 3</a>. In this zone, the radial velocity is not null and the normal velocity and the pressure are not constant in a cross section,
   <li class=itemize>
      after the pink zone, the pressure and normal velocity become uniform over the duct section, while the radial velocity is zero while
      <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
         <mi>λ</mi>
         <mo class=MathClass-rel>≫</mo>
         <msqrt>
            <mrow>
               <msub>
                  <mrow>
                     <mi>S</mi>
                  </mrow>
                  <mrow>
                     <mi>w</mi>
                  </mrow>
               </msub>
               <mo class=MathClass-bin>∕</mo>
               <mi>π</mi>
            </mrow>
         </msqrt>
      </math>.
</ul>
<figure class=figure>
   <img alt="Figure 3.Rigid piston vibrating in a waveguide with a section change." src=${config.ASSETS_URL}/04/11/theory/figure-3.svg>
   <figcaption class=caption>Figure 3.Rigid piston vibrating in a waveguide with a section change.</figcaption>
</figure>
<p>
   To summarise, there is a transition zone in the vicinity of the interface where the radial velocity leads to an acceleration without compression of the air. This significantly increases the local kinetic energy density. The reaction on the piston then results in an inertial force which can be expressed by using an additional equivalent acoustic mass. Note that losses due to this sudden expansion of the cross-sectional area also exist, but they are usually very small and are often neglected in electroacoustic applications. Conservation of flow rate is still valid, therefore we have on the interface
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>Q</mi>
      <mo class=MathClass-rel>=</mo>
      <msub>
         <mrow>
            <mi class=MathClass-op>∫</mi>
            <mo>⁡</mo>
         </mrow>
         <mrow>
            <msub>
               <mrow>
                  <mi>S</mi>
               </mrow>
               <mrow>
                  <mi>d</mi>
               </mrow>
            </msub>
         </mrow>
      </msub>
      <mi>V</mi>
      <mi mathvariant=italic>dS</mi>
      <mo class=MathClass-rel>=</mo>
      <msub>
         <mrow>
            <mi>S</mi>
         </mrow>
         <mrow>
            <mi>d</mi>
         </mrow>
      </msub>
      <mi>V</mi>
   </math>.<br class=newline>
<p>
   The equivalent electrical network of a coupling with a section change is shown in <a href="#" data-img-alt="Figure 4. Equivalent network of an acousto-mechanical interface with a surface change." data-img-src=${config.ASSETS_URL}/04/11/theory/figure-4.svg>Figure 4</a>. It corresponds to a transformer and an additional acoustical mass.<br class=newline>
   <figure class=figure>
<p><img alt="Figure 4. Equivalent network of an acousto-mechanical interface with a surface change." src=${config.ASSETS_URL}/04/11/theory/figure-4.svg><figcaption class=caption>Figure 4. Equivalent network of an acousto-mechanical interface with a surface change.</figcaption></figure>
<p>
   The pressure
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>p</mi>
   </math>
   shown in the diagram is the pressure that is obtained after the transition zone (<math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>x</mi>
      <mo class=MathClass-rel>></mo>
      <mi mathvariant=italic>dL</mi>
   </math>). This pressure is different from that which would exist without a change in cross-section.
<h3 class=sectionHead>Link with the baffled planar piston</h3>
<p>
   If the cross-sectional area of the tube in <a href="#" data-img-alt="Figure 3.Rigid piston vibrating in a waveguide with a section change." data-img-src=${config.ASSETS_URL}/04/11/theory/figure-3.svg>Figure 3</a> is increased considerably (<math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>S</mi>
         </mrow>
         <mrow>
            <mi>w</mi>
         </mrow>
      </msub>
      <mo class=MathClass-rel>→</mo>
      <mi>∞</mi></math>), the configuration of the baffled planar piston is recovered. In this case, the effect of the change in cross-section takes the form of the radiation impedance of the baffled planar piston (see <a href="${linkLesson0406}" target="_blank">Lesson 6</a>), which can be expressed at low frequency by
<table class=equation>
   <tr>
      <td style="font-size: 12px;">
         <math class=equation xmlns=http://www.w3.org/1998/Math/MathML display=block>
            <msub>
               <mrow>
                  <mi>Z</mi>
               </mrow>
               <mrow>
                  <mi mathvariant=italic>ar</mi>
               </mrow>
            </msub>
            <mo class=MathClass-rel>=</mo>
            <msub>
               <mrow>
                  <mi>R</mi>
               </mrow>
               <mrow>
                  <mi mathvariant=italic>ar</mi>
               </mrow>
            </msub>
            <mo class=MathClass-bin>+</mo>
            <mi mathvariant=italic>jω</mi>
            <msub>
               <mrow>
                  <mi>M</mi>
               </mrow>
               <mrow>
                  <mi mathvariant=italic>ar</mi>
               </mrow>
            </msub>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>ρ</mi>
                     </mrow>
                     <mrow>
                        <mn>0</mn>
                     </mrow>
                  </msub>
                  <mi>c</mi>
               </mrow>
               <mrow>
                  <mn>2</mn>
                  <msub>
                     <mrow>
                        <mi>S</mi>
                     </mrow>
                     <mrow>
                        <mi>d</mi>
                     </mrow>
                  </msub>
               </mrow>
            </mfrac>
            <msup>
               <mrow>
                  <mo class=MathClass-open>(</mo>
                  <mi mathvariant=italic>ka</mi>
                  <mo class=MathClass-close>)</mo>
               </mrow>
               <mrow>
                  <mn>2</mn>
               </mrow>
            </msup>
            <mo class=MathClass-bin>+</mo>
            <mi mathvariant=italic>jω</mi>
            <mfrac>
               <mrow>
                  <mn>0</mn>
                  <mo class=MathClass-punc>.</mo>
                  <mn>85</mn>
                  <msub>
                     <mrow>
                        <mi>ρ</mi>
                     </mrow>
                     <mrow>
                        <mn>0</mn>
                     </mrow>
                  </msub>
                  <mi>a</mi>
               </mrow>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>S</mi>
                     </mrow>
                     <mrow>
                        <mi>d</mi>
                     </mrow>
                  </msub>
               </mrow>
            </mfrac>
         </math>
      <td class=eq-no>(3)
</table>
<p>
   At very low frequencies (<math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>λ</mi>
      <mo class=MathClass-rel>≫</mo>
      <mi>a</mi>
   </math>),
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>Z</mi>
         </mrow>
         <mrow>
            <mi mathvariant=italic>ar</mi>
         </mrow>
      </msub>
      <mo class=MathClass-rel>≃</mo>
      <mi mathvariant=italic>jω</mi>
      <msub>
         <mrow>
            <mi>M</mi>
         </mrow>
         <mrow>
            <mi mathvariant=italic>ar</mi>
         </mrow>
      </msub>
   </math>. As the piston has two sides, the radiation impedance is used for each side, resulting in the equivalent circuit shown in <a href="#" data-img-alt="Figure 5. Equivalent network of a baffled planar piston." data-img-src=${config.ASSETS_URL}/04/11/theory/figure-5.svg>Figure 5</a>. Note that the pressures on the front side
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>p</mi>
         </mrow>
         <mrow>
            <mi>f</mi>
         </mrow>
      </msub>
   </math>
   and back side
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>p</mi>
         </mrow>
         <mrow>
            <mi>b</mi>
         </mrow>
      </msub>
   </math>
   of the piston are in phase opposition
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>p</mi>
         </mrow>
         <mrow>
            <mi>f</mi>
         </mrow>
      </msub>
      <mo class=MathClass-rel>=</mo>
      <mo class=MathClass-bin>−</mo>
      <msub>
         <mrow>
            <mi>p</mi>
         </mrow>
         <mrow>
            <mi>b</mi>
         </mrow>
      </msub>
   </math>.
   <figure class=figure>
<p><img alt="Figure 5. Equivalent network of a baffled planar piston." src=${config.ASSETS_URL}/04/11/theory/figure-5.svg><figcaption class=caption>Figure 5. Equivalent network of a baffled planar piston.</figcaption></figure>
<h3 class=sectionHead>Application</h3>
<p>
   A planar diaphragm is mounted in a closed box of volume
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>V</mi>
         </mrow>
         <mrow>
            <mi>b</mi>
         </mrow>
      </msub>
   </math>
   which in turn is mounted in a large baffle (see <a href="#" data-img-src=${config.ASSETS_URL}/04/11/theory/figure-6.svg data-img-alt="Figure 6. Baffled planar piston mounted in a box.">Figure 6</a>). The diaphragm alone can be assimilated to a one-degree-of-freedom system with mass
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>M</mi>
         </mrow>
         <mrow>
            <mi mathvariant=italic>md</mi>
         </mrow>
      </msub>
   </math>, compliance
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>C</mi>
         </mrow>
         <mrow>
            <mi mathvariant=italic>md</mi>
         </mrow>
      </msub>
   </math>
   and mechanical resistance
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>R</mi>
         </mrow>
         <mrow>
            <mi mathvariant=italic>md</mi>
         </mrow>
      </msub>
   </math>. The diaphragm is excited by the force
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>F</mi>
         </mrow>
         <mrow>
            <mi>e</mi>
         </mrow>
      </msub>
   </math>
   and vibrates with a velocity
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>V</mi>
   </math>.<br class=newline>
   <figure class=figure>
<p><img src=${config.ASSETS_URL}/04/11/theory/figure-6.svg alt="Figure 6. Baffled planar piston mounted in a box."><figcaption class=caption>Figure 6. Baffled planar piston mounted in a box.</figcaption></figure>
<p>
   This diaphragm is both coupled to the rear volume and to the outside air. The equivalent network of the complete system is drawn in <a href="#" data-img-src=${config.ASSETS_URL}/04/11/theory/figure-7.svg data-img-alt="Figure 7. Equivalent network of a diaphragm mounted in a baffle and loaded by a box.">Figure 7</a>. On the left hand side, we have the mechanical part including the excitation force
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>F</mi>
         </mrow>
         <mrow>
            <mi>e</mi>
         </mrow>
      </msub>
   </math>
   and the diaphragm impedance
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>Z</mi>
         </mrow>
         <mrow>
            <mi>m</mi>
         </mrow>
      </msub>
      <mo class=MathClass-rel>=</mo>
      <msub>
         <mrow>
            <mi>R</mi>
         </mrow>
         <mrow>
            <mi mathvariant=italic>md</mi>
         </mrow>
      </msub>
      <mo class=MathClass-bin>+</mo>
      <mi mathvariant=italic>jω</mi>
      <msub>
         <mrow>
            <mi>M</mi>
         </mrow>
         <mrow>
            <mi mathvariant=italic>md</mi>
         </mrow>
      </msub>
      <mo class=MathClass-bin>+</mo>
      <mfrac>
         <mrow>
            <mn>1</mn>
         </mrow>
         <mrow>
            <mi mathvariant=italic>jω</mi>
            <msub>
               <mrow>
                  <mi>C</mi>
               </mrow>
               <mrow>
                  <mi mathvariant=italic>md</mi>
               </mrow>
            </msub>
         </mrow>
      </mfrac>
   </math>, while on the right hand side the capacitance
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>C</mi>
         </mrow>
         <mrow>
            <mi mathvariant=italic>ab</mi>
         </mrow>
      </msub>
      <mo class=MathClass-rel>=</mo>
      <msub>
         <mrow>
            <mi>V</mi>
         </mrow>
         <mrow>
            <mi>b</mi>
         </mrow>
      </msub>
      <mo class=MathClass-bin>∕</mo>
      <mo class=MathClass-open>(</mo>
      <mi>ρ</mi>
      <msup>
         <mrow>
            <mi>c</mi>
         </mrow>
         <mrow>
            <mn>2</mn>
         </mrow>
      </msup>
      <mo class=MathClass-close>)</mo>
   </math>
   represents the acoustical compliance of the closed volume and the two acoustic masses
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>M</mi>
         </mrow>
         <mrow>
            <mi>a</mi>
         </mrow>
      </msub>
   </math>
   and
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>M</mi>
         </mrow>
         <mrow>
            <mi mathvariant=italic>ar</mi>
         </mrow>
      </msub>
   </math>
   respectively represent the cross-sectional change seen by the rear side of the membrane and the radiation mass loading of the front side.<br class=newline>
   <figure class=figure>
<p><img src=${config.ASSETS_URL}/04/11/theory/figure-7.svg alt="Figure 7. Equivalent network of a diaphragm mounted in a baffle and loaded by a box."><figcaption class=caption>Figure 7. Equivalent network of a diaphragm mounted in a baffle and loaded by a box.</figcaption></figure>
<p>
   By using Eq. (2) which relates the acoustical impedance to the mechanical impedance, it is possible to bring the mechanical domain into the acoustical one, and vice versa. The equivalent network of the full system in the acoustical domain is plotted in <a href="#" data-img-src=${config.ASSETS_URL}/04/11/theory/figure-8.svg data-img-alt="Figure 8. Equivalent network of a diaphragm mounted in a baffle and loaded by a box in the acoustical domain.">Figure 8</a>, where
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>M</mi>
         </mrow>
         <mrow>
            <mi mathvariant=italic>ad</mi>
         </mrow>
      </msub>
      <mo class=MathClass-rel>=</mo>
      <msub>
         <mrow>
            <mi>M</mi>
         </mrow>
         <mrow>
            <mi mathvariant=italic>md</mi>
         </mrow>
      </msub>
      <mo class=MathClass-bin>∕</mo>
      <msubsup>
         <mrow>
            <mi>S</mi>
         </mrow>
         <mrow>
            <mi>d</mi>
         </mrow>
         <mrow>
            <mn>2</mn>
         </mrow>
      </msubsup>
   </math>,
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>R</mi>
         </mrow>
         <mrow>
            <mi mathvariant=italic>ad</mi>
         </mrow>
      </msub>
      <mo class=MathClass-rel>=</mo>
      <msub>
         <mrow>
            <mi>R</mi>
         </mrow>
         <mrow>
            <mi mathvariant=italic>md</mi>
         </mrow>
      </msub>
      <mo class=MathClass-bin>∕</mo>
      <msubsup>
         <mrow>
            <mi>S</mi>
         </mrow>
         <mrow>
            <mi>d</mi>
         </mrow>
         <mrow>
            <mn>2</mn>
         </mrow>
      </msubsup>
   </math>
   and
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>C</mi>
         </mrow>
         <mrow>
            <mi mathvariant=italic>ad</mi>
         </mrow>
      </msub>
      <mo class=MathClass-rel>=</mo>
      <msubsup>
         <mrow>
            <mi>S</mi>
         </mrow>
         <mrow>
            <mi>d</mi>
         </mrow>
         <mrow>
            <mn>2</mn>
         </mrow>
      </msubsup>
      <msub>
         <mrow>
            <mi>C</mi>
         </mrow>
         <mrow>
            <mi mathvariant=italic>md</mi>
         </mrow>
      </msub>
   </math>. In the case where the cross-sectional area of the volume is much larger than the size of the diaphragm,
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>M</mi>
         </mrow>
         <mrow>
            <mi>a</mi>
         </mrow>
      </msub>
      <mo class=MathClass-rel>≃</mo>
      <msub>
         <mrow>
            <mi>M</mi>
         </mrow>
         <mrow>
            <mi mathvariant=italic>ar</mi>
         </mrow>
      </msub>
   </math>
   provides a reasonable approximation.<br class=newline>
   <figure class=figure>
<p><img src=${config.ASSETS_URL}/04/11/theory/figure-8.svg alt="Figure 8. Equivalent network of a diaphragm mounted in a baffle and loaded by a box in the acoustical domain."><figcaption class=caption>Figure 8. Equivalent network of a diaphragm mounted in a baffle and loaded by a box in the acoustical domain.</figcaption></figure>
<p>
   The circuit of <a href="#" data-img-src=${config.ASSETS_URL}/04/11/theory/figure-8.svg data-img-alt="Figure 8. Equivalent network of a diaphragm mounted in a baffle and loaded by a box in the acoustical domain.">Figure 8</a> can be used to calculate the velocity
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>V</mi>
   </math>
   as a function of the input force
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>F</mi>
         </mrow>
         <mrow>
            <mi>e</mi>
         </mrow>
      </msub>
   </math>. Thanks to the electromechanical and electroacoustic analogies, the resolution of the studied coupled system is rather simple. Especially since the system has an impedance in the form of a classical RLC circuit.<br class=newline>We notice that if
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>M</mi>
         </mrow>
         <mrow>
            <mi>a</mi>
         </mrow>
      </msub>
      <mo class=MathClass-rel>≃</mo>
      <msub>
         <mrow>
            <mi>M</mi>
         </mrow>
         <mrow>
            <mi mathvariant=italic>ar</mi>
         </mrow>
      </msub>
   </math>, the addition of the closed enclosure increases the resonance frequency of the system. Indeed, we go from
<table class=equation>
   <tr>
      <td>
         <math class=equation xmlns=http://www.w3.org/1998/Math/MathML display=block>
            <msub>
               <mrow>
                  <mi>f</mi>
               </mrow>
               <mrow>
                  <mi>d</mi>
               </mrow>
            </msub>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <mn>1</mn>
               </mrow>
               <mrow>
                  <mn>2</mn>
                  <mi>π</mi>
                  <msqrt>
                     <mrow>
                        <msub>
                           <mrow>
                              <mi>M</mi>
                           </mrow>
                           <mrow>
                              <mi mathvariant=italic>as</mi>
                           </mrow>
                        </msub>
                        <msub>
                           <mrow>
                              <mi>C</mi>
                           </mrow>
                           <mrow>
                              <mi mathvariant=italic>ad</mi>
                           </mrow>
                        </msub>
                     </mrow>
                  </msqrt>
               </mrow>
            </mfrac>
         </math>
      <td class=eq-no>(4)
</table>
<p>to
<table class=equation>
   <tr>
      <td>
         <math class=equation xmlns=http://www.w3.org/1998/Math/MathML display=block>
            <msub>
               <mrow>
                  <mi>f</mi>
               </mrow>
               <mrow>
                  <mi>s</mi>
               </mrow>
            </msub>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <mn>1</mn>
               </mrow>
               <mrow>
                  <mn>2</mn>
                  <mi>π</mi>
                  <msqrt>
                     <mrow>
                        <msub>
                           <mrow>
                              <mi>M</mi>
                           </mrow>
                           <mrow>
                              <mi mathvariant=italic>as</mi>
                           </mrow>
                        </msub>
                        <msub>
                           <mrow>
                              <mi>C</mi>
                           </mrow>
                           <mrow>
                              <mi mathvariant=italic>as</mi>
                           </mrow>
                        </msub>
                     </mrow>
                  </msqrt>
               </mrow>
            </mfrac>
         </math>
      <td class=eq-no>(5)
</table>
<p>
   where
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>f</mi>
         </mrow>
         <mrow>
            <mi>d</mi>
         </mrow>
      </msub>
   </math>
   is the resonance frequency of the diaphragm in a baffle,
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>f</mi>
         </mrow>
         <mrow>
            <mi>s</mi>
         </mrow>
      </msub>
   </math>
   is the resonance frequency of the complete system,
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>M</mi>
         </mrow>
         <mrow>
            <mi mathvariant=italic>as</mi>
         </mrow>
      </msub>
      <mo class=MathClass-rel>=</mo>
      <msub>
         <mrow>
            <mi>M</mi>
         </mrow>
         <mrow>
            <mi mathvariant=italic>ad</mi>
         </mrow>
      </msub>
      <mo class=MathClass-bin>+</mo>
      <mn>2</mn>
      <msub>
         <mrow>
            <mi>M</mi>
         </mrow>
         <mrow>
            <mi mathvariant=italic>ar</mi>
         </mrow>
      </msub>
   </math>
   and
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>C</mi>
         </mrow>
         <mrow>
            <mi mathvariant=italic>as</mi>
         </mrow>
      </msub>
      <mo class=MathClass-rel>=</mo>
      <mfrac>
         <mrow>
            <msub>
               <mrow>
                  <mi>C</mi>
               </mrow>
               <mrow>
                  <mi mathvariant=italic>ad</mi>
               </mrow>
            </msub>
            <msub>
               <mrow>
                  <mi>C</mi>
               </mrow>
               <mrow>
                  <mi mathvariant=italic>ab</mi>
               </mrow>
            </msub>
         </mrow>
         <mrow>
            <msub>
               <mrow>
                  <mi>C</mi>
               </mrow>
               <mrow>
                  <mi mathvariant=italic>ad</mi>
               </mrow>
            </msub>
            <mo class=MathClass-bin>+</mo>
            <msub>
               <mrow>
                  <mi>C</mi>
               </mrow>
               <mrow>
                  <mi mathvariant=italic>ab</mi>
               </mrow>
            </msub>
         </mrow>
      </mfrac>
      <mo class=MathClass-rel>=</mo>
      <msub>
         <mrow>
            <mi>C</mi>
         </mrow>
         <mrow>
            <mi mathvariant=italic>ad</mi>
         </mrow>
      </msub>
      <mfrac>
         <mrow>
            <mn>1</mn>
         </mrow>
         <mrow>
            <mn>1</mn>
            <mo class=MathClass-bin>+</mo>
            <msub>
               <mrow>
                  <mi>C</mi>
               </mrow>
               <mrow>
                  <mi mathvariant=italic>ad</mi>
               </mrow>
            </msub>
            <mo class=MathClass-bin>∕</mo>
            <msub>
               <mrow>
                  <mi>C</mi>
               </mrow>
               <mrow>
                  <mi mathvariant=italic>ab</mi>
               </mrow>
            </msub>
         </mrow>
      </mfrac>
      <mo class=MathClass-rel><</mo>
      <msub>
         <mrow>
            <mi>C</mi>
         </mrow>
         <mrow>
            <mi mathvariant=italic>ad</mi>
         </mrow>
      </msub>
   </math>. As
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>C</mi>
         </mrow>
         <mrow>
            <mi mathvariant=italic>as</mi>
         </mrow>
      </msub>
      <mo class=MathClass-rel><</mo>
      <msub>
         <mrow>
            <mi>C</mi>
         </mrow>
         <mrow>
            <mi mathvariant=italic>ad</mi>
         </mrow>
      </msub>
   </math>, we have
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>f</mi>
         </mrow>
         <mrow>
            <mi>s</mi>
         </mrow>
      </msub>
      <mo class=MathClass-rel>></mo>
      <msub>
         <mrow>
            <mi>f</mi>
         </mrow>
         <mrow>
            <mi>d</mi>
         </mrow>
      </msub>
   </math>. This is consistent with the addition of a stiffness term increasing the resonant frequency.
<h3 class=sectionHead>Conclusion</h3>
<p>
   In this Lesson, the coupling between a mechanical system and an acoustical system was introduced. This has been written in the form of an equivalent electrical circuit. Different types of acoustic circuit were used, a duct with the same surface area as the mechanical system, a duct with a larger surface area and finally a closed enclosure. This last case will be particularly useful when we will discuss the modelling of closed-box systems in <a href="${linkLesson0420}" target="_blank">Lesson 20</a>.
<h3 class=likesectionHead>Glossary</h3>
<p>
<dl>
   <dt>RLC
   <dd>Resistor, Inductor, Capacitor
</dl>
<h3 class=sectionHead>References</h3>
<dl class=thebibliography>
   <dt class=thebibliography id=X0-Beranek2012>[1]
   <dd class=thebibliography id=bib-1>
      <p>Leo Beranek and Tim Mellow. <span class=cmti-10>Acoustics: sound fields and transducers</span>. Academic Pres, 2012. <span class=cmcsc-10><span class=small-caps>isbn</span></span>: 9780123914866.
   <dt class=thebibliography id=X0-Herzog>[2]
   <dd class=thebibliography id=bib-2>
      <p>Philippe Herzog and Bruno Gazengel. <span class=cmti-10>Mechano-acoustical coupling</span>. <a class=url href=http://learn-electroacoustics.eu/cours/Grain3.1/co/M3G1_FR_web.html><span class=cmtt-10>http://learn-electroacoustics.eu/cours/Grain3.1/co/M3G1_FR_web.html</span></a>. Online course, 2016.
   <dt class=thebibliography id=X0-Rossi1988>[3]
   <dd class=thebibliography id=bib-3>
      <p>Mario Rossi. <span class=cmti-10>Acoustics and Electroacoustics</span>. Artech House, 1988. <span class=cmcsc-10><span class=small-caps>isbn</span></span>: 978-0890062555.
</dl>`;

export default theory;
